<template>
  <div
    class="fy-bp-wrapper"
    :class="{
      'page-mode': mode === 'page',
      'screen-mode': mode === 'screen',
    }"
    @click.stop="() => true"
    @mousedown.stop="$emit('close')"
  >
    <div
      ref="container"
      v-click-outside="vsoConfig"
      class="fy-bp-container container"
      :style="{...containerStyle}"
      @mousedown.stop="() => true"
    >
      <div
        v-if="closeButton"
        class="fy-bp-close clickable"
        @click="close"
      >
        <FontAwesomeIcon icon="times" />
      </div>
      <div
        v-if="!hideHeader"
        class="fy-bp-header translate"
      >
        <div
          v-if="advancedTitle"
          class="fy-bp-left-holder"
        >
          <slot name="lheader" />
        </div>
        <div
          class="fy-bp-title text-upper translate"
          :class="{advanced: advancedTitle}"
          v-html="title"
        />
        <div
          v-if="advancedTitle"
          class="fy-bp-right-holder"
        >
          <slot name="rheader" />
        </div>
      </div>
      <div
        class="fy-bp-main"
        :class="{scrolling: (fixed && !blockHeightCalc) || scrollContent}"
      >
        <div
          v-if="fixedRequired"
          class="fy-bp-fixed"
          :style="{'max-height': fixedHeight}"
        >
          <slot name="fixed" />
        </div>
        <div
          v-if="tableHeaderRequired"
          class="fy-bp-table-header"
        >
          <slot name="th" />
        </div>
        <div
          class="fy-bp-scrollable"
          :class="{fixed}"
          :style="scrollableStyle"
        >
          <slot />
        </div>
      </div>
      <div
        v-if="footerRequired"
        class="fy-bp-footer"
      >
        <slot name="footer" />
      </div>
    </div>
  </div>
</template>
<script>
import vClickOutside from 'click-outside-vue3';

/**
 * BasePopup properties and slots.
 * @prop {Boolean} closeButton - Adds close "cross" button to upper-right corner.
 * @prop {String} containerStyle - Styles for main container.
 * @prop {Boolean} fixedRequired - Allows to use fixed block between header and scrollable part.
 * @prop {Boolean} footerRequired - Allows to use footer block.
 * @prop {String} mode - Switchs popup between following modes:
 * ***** page - overlay covers only page, header is accessible;
 * ***** screen - overlay covers entire screen, blocking user from access to header in one click.
 * @prop {Boolean} preventScroll - Prevents scrolling in application, makes scrollable div fixed.
 * @prop {String} title - popup title.
 *
 * @slot - slot in scrollable part.
 * @slot fixed - slot in fixed part.
 * @slot footer - slot in footer.
 * @slot lheader - slot to the left of title.
 * @slot rheader - slot to the right of title.
 */
export default {
  name: 'BasePopup',
  directives: {
    clickOutside: vClickOutside.directive,
  },
  props: {
    hideHeader: Boolean,
    scrollContent: Boolean,
    advancedTitle: Boolean,
    blockHeightCalc: Boolean,
    closeButton: Boolean,
    fixedRequired: Boolean,
    footerRequired: Boolean,
    mode: {
      type: String,
      default: 'page',
    },
    containerStyle: {
      type: Object,
      default: () => {},
    },
    fixedHeight: {
      type: String,
      default: '',
    },
    needZoom: Boolean,
    preventScroll: Boolean,
    tableHeaderRequired: Boolean,
    scrollableStyle: {
      type: [Object, String],
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
  },
  emits: ['enter', 'mounted', 'close', 'return', 'fixed'],
  data() {
    return {
      fixed: false,
      keydown: () => true,
      vsoConfig: {
        handler: this.close,
        events: ['mousedown'],
      },
    };
  },
  mounted() {
    if (!this.blockHeightCalc) {
      window.addEventListener('resize', this.resize);
      this.resize();
    }
    this.keydown = document.onkeydown;
    document.onkeydown = (e) => {
      if (
        [37, 38, 39, 40].includes(e.keyCode) &&
        !e.target.closest('input') &&
        !e.target.closest('textarea')
      ) {
        e.preventDefault();
      }
      const evt = e || window.event;
      let isEscape = false;
      if ('key' in evt) {
        isEscape = evt.key === 'Escape' || evt.key === 'Esc';
        if (evt.key === 'Enter') {
          this.$emit('enter', evt);
        }
      } else {
        isEscape = evt.keyCode === 27;
      }
      if (isEscape) {
        if (this.allowClose === undefined || !!this.allowClose) {
          this.$emit('close');
        }
      }
    };
    this.$emit('mounted');
  },
  beforeUnmount() {
    document.onkeydown = this.keydown;
    if (!this.blockHeightCalc) {
      window.removeEventListener('resize', this.resize);
    }
  },
  methods: {
    onScroll(e) {
      if (!this.keepScroll) {
        e.preventDefault();
      }
    },
    close(e) {
      if (
        this.keepOpened ||
        (e.target instanceof Element && e.target.closest('.notification'))
      ) {
        return;
      }
      if (
        e.target instanceof Element &&
        (!e.target.closest('.dropdown') || e.target.closest('.close-popup'))
      ) {
        this.$emit('close', e);
      }
    },
    goBack(e) {
      this.$emit('return', e);
    },
    resize() {
      if (this.containerStyle && this.containerStyle.height) {
        const height = Number(this.containerStyle.height.replace(/\D/g, ''));
        this.fixed = height + 20 > this.$refs.container.clientHeight;
      }
      this.$emit('fixed', this.fixed);
    },
  },
};
</script>
<style lang="scss">
.fy-bp-wrapper {
  display: flex;
  background: rgba(0, 0, 0, 0.7);
  position: fixed;
  inset: 0;
  z-index: 300;
  align-items: center;
  justify-content: center;
  .fy-bp-container {
    display: flex;
    flex-direction: column;
    padding: var(--px10);
    max-height: calc(100% - var(--px100));
    position: relative;
    .fy-bp-close {
      position: absolute;
      top: var(--px10);
      right: var(--px10);
      height: var(--px20);
      width: var(--px20);
      font-size: var(--px20);
      color: $gray;
    }
    .fy-bp-header {
      display: flex;
      padding: var(--px30) 0 var(--px15);
      margin: 0 var(--px30);
      width: calc(100% - var(--px60));
      .fy-bp-left-holder,
      .fy-bp-right-holder {
        display: flex;
        flex: 1;
        position: relative;
      }
      .fy-bp-title {
        display: flex;
        align-items: center;
        @extend %text-font-medium;
        justify-self: center;
        justify-content: center;
        text-align: center;
        min-height: var(--px30);
        max-height: var(--px30);
        margin: 0 auto;
        font-weight: 600;
        &.advanced {
          margin: 0 var(--px30);
        }
      }
    }
    .fy-bp-main {
      display: flex;
      flex-direction: column;
      flex: 1;
      padding: var(--px15) var(--px30);
      margin-bottom: var(--px30);
      height: calc(100% - var(--px290));
      width: calc(100% - var(--px60));
      &.scrolling {
        overflow: auto;
      }
      .fy-bp-fixed {
        display: flex;
        flex-direction: column;
        margin-bottom: var(--px30);
        max-height: var(--px200);
        position: relative;
        width: 100%;
      }
      .fy-bp-table-header {
        display: flex;
        flex-direction: column;
        position: relative;
        width: 100%;
      }
      .fy-bp-scrollable {
        display: flex;
        flex-direction: column;
        height: 100%;
        position: relative;
        flex: 1;
      }
    }
    .fy-bp-footer {
      display: flex;
      flex-direction: column;
      width: calc(100% - var(--px60));
      padding-bottom: var(--px30);
      margin: 0 var(--px30);
    }
  }
}
</style>
